import React, { useState } from "react";
import { Form, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import { LeaveMapping } from "./Dashboard";
import {applyLeave} from '../../services/leaveServices'

const LeaveForm = ({ leaves, onCancel, getLeave,employeeId }) => {
  const [leaveData, setLeaveData] = useState({
    reason: "",
    type: "",
    fromDate: "",
    toDate: "",
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);


  // Handle form data change
  const handleDataChange = ({ name, value }) => {
    // Check if the field is `fromDate` or `toDate`
    if (["fromDate", "toDate"].includes(name)) {
      const selectedDate = new Date(value);
      const currentDate = new Date();

      // Set the current date time to 00:00:00 for comparison
      currentDate.setHours(0, 0, 0, 0);

      if (selectedDate < currentDate) {
        toast.error("The selected date cannot be earlier than today.");
        return; // Exit the function if validation fails
      }
    }

    // Update the state
    setLeaveData((curr) => ({ ...curr, [name]: value }));
  };

  // Calculate the number of leave days requested
  const calculateLeaveDays = () => {
    const fromDate = new Date(leaveData.fromDate);
    const toDate = new Date(leaveData.toDate);
    if (!isNaN(fromDate) && !isNaN(toDate) && fromDate <= toDate) {
      return Math.ceil((toDate - fromDate) / (1000 * 60 * 60 * 24)) + 1; // Including the fromDate
    }
    return 0;
  };

  // Function to handle field focus and clear specific errors
  const handleFocus = (fieldName) => {
    setErrors((currentErrors) => {
      const updatedErrors = { ...currentErrors };
      delete updatedErrors[fieldName]; // Remove error for the focused field
      return updatedErrors;
    });
  };

  // Validate the form
  const validateForm = () => {
    const errors = {};
    if (!leaveData.reason.trim()) errors.reason = "Reason is required.";
    if (!leaveData.type) errors.type = "Please select a leave type.";
    if (!leaveData.fromDate) errors.fromDate = "From date is required.";
    if (!leaveData.toDate) errors.toDate = "To date is required.";

    const leaveDays = calculateLeaveDays();
    if (leaveDays > 0 && leaveData.type) {
      const remainingLeaves = leaves[LeaveMapping[leaveData.type]];
      if (leaveDays > remainingLeaves) {
        errors.leaveDays = `Requested days (${leaveDays}) exceed remaining leave (${remainingLeaves}).`;
      }
    }

    setErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle leave form submission
  const handleFormData = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    try {
      setLoading(true);
      await applyLeave(leaveData); // Call API function
      toast.success("Leave form submitted!");
      // onSubmit(leaveData);
      getLeave(employeeId)
      onCancel();
    } catch (error) {
      toast.error(error.message || "Unable to submit leave form");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="px-3 py-4">
      <div>
        <p className="text-xl text-center font-medium">Leave Form</p>
      </div>
      <Form onSubmit={handleFormData} className="flex flex-col gap-y-3">
        {/* Reason */}
        <Form.Control
          type="text"
          name="reason"
          placeholder="Reason for leave"
          value={leaveData.reason}
          onFocus={({ target }) => handleFocus(target.name)}
          onChange={({ target }) => handleDataChange(target)}
          isInvalid={!!errors.reason}
        />
        <Form.Control.Feedback type="invalid">
          {errors.reason}
        </Form.Control.Feedback>

        {/* Leave Type */}
        <div>
          <Form.Select
            value={leaveData.type}
            name="type"
            onChange={({ target }) => handleDataChange(target)}
            onFocus={({ target }) => handleFocus(target.name)}
            isInvalid={!!errors.type}
          >
            <option value="" hidden>
              Select Leave Type
            </option>
            <option value="Sick">Sick</option>
            <option value="Paid">Paid</option>
          </Form.Select>
          {leaveData.type && (
            <p>
              <strong>Remaining Leaves: </strong>
              {leaves[leaveData.type]}
            </p>
          )}
          <Form.Control.Feedback type="invalid">
            {errors.type}
          </Form.Control.Feedback>
        </div>

        {/* From Date */}
        <Form.Label htmlFor="fromDate">
          From:
          <Form.Control
            type="date"
            min={new Date().toISOString().split("T")[0]}  // Ensure that today's date is the minimum
            name="fromDate"
            value={leaveData.fromDate}
            onChange={({ target }) => handleDataChange(target)}
            onFocus={({ target }) => handleFocus(target.name)}
            isInvalid={!!errors.fromDate}
          />
          <Form.Control.Feedback type="invalid">
            {errors.fromDate}
          </Form.Control.Feedback>
        </Form.Label>

        {/* To Date */}
        <Form.Label htmlFor="toDate">
          To:
          <Form.Control
            type="date"
            name="toDate"
            value={leaveData.toDate}
            onChange={({ target }) => handleDataChange(target)}
            onFocus={({ target }) => handleFocus(target.name)}
            min={leaveData.fromDate} // This ensures the end date is always greater than or equal to the start date
            isInvalid={!!errors.toDate}
          />
          <Form.Control.Feedback type="invalid">
            {errors.toDate}
          </Form.Control.Feedback>
        </Form.Label>

        {/* Leave Days Validation */}
        {errors.leaveDays && <p className="text-danger">{errors.leaveDays}</p>}

        {/* Actions */}
        <div className="flex flex-row justify-end gap-x-2">
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button variant="primary" type="submit">
            Submit
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default LeaveForm;
